import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_CONFIG, getAuthHeader } from '../../api/config';
import './AffiliationEmail.css';

const AffiliationEmail = () => {
  const [emails, setEmails] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    statut: 'actif',
    societe: 'legrand'
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSociete, setFilterSociete] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  // Configuration API pour Legrand
  const headers = {
    ...API_CONFIG.headers,
    ...getAuthHeader('legrand')
  };

  // Charger tous les emails
  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.baseUrl}/api/legrand/affiliation/emails`,
        { headers }
      );
      // Filtrer uniquement les emails de Legrand
      const legrandEmails = response.data.filter(e => e.societe === 'legrand');
      setEmails(legrandEmails);
      setError('');
    } catch (err) {
      setError('Erreur lors du chargement des emails');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await axios.put(
          `${API_CONFIG.baseUrl}/api/legrand/affiliation/emails/${editingId}`,
          { ...formData, societe: 'legrand' },
          { headers }
        );
        setEditingId(null);
      } else {
        await axios.post(
          `${API_CONFIG.baseUrl}/api/legrand/affiliation/emails`,
          { ...formData, societe: 'legrand' },
          { headers }
        );
      }

      setFormData({
        email: '',
        statut: 'actif',
        societe: 'legrand'
      });
      fetchEmails();
      setError('');
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError('Cet email existe déjà');
      } else {
        setError('Erreur lors de l\'enregistrement');
      }
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (email) => {
    setFormData({
      email: email.email,
      statut: email.statut,
      societe: 'legrand'
    });
    setEditingId(email.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet email?')) {
      setLoading(true);
      try {
        await axios.delete(
          `${API_CONFIG.baseUrl}/api/legrand/affiliation/emails/${id}`,
          { headers }
        );
        fetchEmails();
        setError('');
      } catch (err) {
        setError('Erreur lors de la suppression');
        console.error('Erreur:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({
      email: '',
      statut: 'actif',
      societe: 'legrand'
    });
    setError('');
  };

  const toggleStatut = async (email) => {
    const newStatut = email.statut === 'actif' ? 'passif' : 'actif';
    setLoading(true);
    try {
      await axios.put(
        `${API_CONFIG.baseUrl}/api/legrand/affiliation/emails/${email.id}`,
        {
          ...email,
          statut: newStatut,
          societe: 'legrand'
        },
        { headers }
      );
      fetchEmails();
      setError('');
    } catch (err) {
      setError('Erreur lors de la mise à jour du statut');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les emails (uniquement Legrand)
  const filteredEmails = emails.filter(email => {
    const matchSearch = email.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === '' || email.statut === filterStatut;

    return matchSearch && matchStatut;
  });

  return (
    <div className="affiliation-email-container">
      <h2>Gestion des Emails d'Affiliation</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Formulaire */}
      <div className="form-section">
        <h3>{editingId ? 'Modifier l\'email' : 'Ajouter un email'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Adresse email"
              required
              className="form-input"
            />
            <input
              type="text"
              name="societe"
              value="LEGRAND"
              disabled
              className="form-input"
              style={{ backgroundColor: '#f0f0f0' }}
            />
            <select
              name="statut"
              value={formData.statut}
              onChange={handleInputChange}
              className="form-select"
            >
              <option value="actif">Actif</option>
              <option value="passif">Passif</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Chargement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </button>
            {editingId && (
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Annuler
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Statistiques */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-value">{emails.length}</div>
          <div className="stat-label">Total des emails</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{emails.filter(e => e.statut === 'actif').length}</div>
          <div className="stat-label">Emails actifs</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{emails.filter(e => e.statut === 'passif').length}</div>
          <div className="stat-label">Emails passifs</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="filter-section">
        <input
          type="text"
          placeholder="Rechercher par email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les statuts</option>
          <option value="actif">Actif</option>
          <option value="passif">Passif</option>
        </select>
      </div>

      {/* Table des emails */}
      <div className="table-section">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Statut</th>
              <th>Date création</th>
              <th>Dernière modification</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center">Chargement...</td>
              </tr>
            ) : filteredEmails.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">Aucun email trouvé</td>
              </tr>
            ) : (
              filteredEmails.map(email => (
                <tr key={email.id}>
                  <td>{email.email}</td>
                  <td>
                    <span
                      className={`status-badge ${email.statut}`}
                      onClick={() => toggleStatut(email)}
                      style={{ cursor: 'pointer' }}
                      title="Cliquez pour changer le statut"
                    >
                      {email.statut}
                    </span>
                  </td>
                  <td>{new Date(email.date_creation).toLocaleDateString('fr-FR')}</td>
                  <td>{new Date(email.date_modification).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(email)}
                      className="btn btn-sm btn-edit"
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(email.id)}
                      className="btn btn-sm btn-delete"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AffiliationEmail;